import type {ChatState, ToolCallState} from '@microsoft/agent-host-protocol'

const MAX_STATE_IDENTITIES = 4096

export function recoverySessionId(resource: string | undefined): string | undefined {
  if (!resource) return
  return resource.startsWith('ahp-session:/') ? resource.slice('ahp-session:/'.length) : resource
}

export function recoveryPendingMessages(state: ChatState | undefined) {
  return [...(state?.queuedMessages ?? []), ...(state?.steeringMessage ? [state.steeringMessage] : [])]
}

export function recoveryTools(state: ChatState | undefined): {
  tools: Array<{turnId: string; tool: ToolCallState}>
  truncated: boolean
} {
  if (!state) return {tools: [], truncated: false}
  const tools: Array<{turnId: string; tool: ToolCallState}> = []
  for (const turn of [...state.turns, ...(state.activeTurn ? [state.activeTurn] : [])]) {
    for (const part of turn.responseParts) {
      if (part.kind !== 'toolCall') continue
      if (tools.length >= MAX_STATE_IDENTITIES) return {tools, truncated: true}
      tools.push({turnId: turn.id, tool: part.toolCall})
    }
  }
  return {tools, truncated: false}
}

export function recoveryStateStructure(state: ChatState | undefined, compatibleWith?: ChatState): string | undefined {
  if (!state || state.turns.length > MAX_STATE_IDENTITIES) return
  const compatible = recoveryTools(compatibleWith)
  if (compatible.truncated) return
  const compatibleTools = new Map(
    compatible.tools.map(({turnId, tool}) => [`${turnId}/${tool.toolCallId}`, tool.status]),
  )
  let identities = state.turns.length
  const turns = []
  for (const turn of [...state.turns, ...(state.activeTurn ? [state.activeTurn] : [])]) {
    const parts = []
    for (const part of turn.responseParts) {
      if (++identities > MAX_STATE_IDENTITIES) return
      parts.push(
        part.kind === 'toolCall'
          ? [
              part.kind,
              part.toolCall.toolCallId,
              compatibleTools.get(`${turn.id}/${part.toolCall.toolCallId}`) === 'streaming' &&
              (part.toolCall.status === 'pending-confirmation' || part.toolCall.status === 'running')
                ? 'streaming'
                : part.toolCall.status,
              part.toolCall.contributor?.kind === 'client' ? part.toolCall.contributor.clientId : undefined,
            ]
          : ['id' in part ? part.id : undefined, part.kind],
      )
    }
    turns.push([turn.id, 'state' in turn ? turn.state : 'active', parts])
  }
  const pending = recoveryPendingMessages(state)
  if (pending.length + identities > MAX_STATE_IDENTITIES) return
  // Compare native identities and state locally. Neither this projection nor
  // content, metadata, tool inputs or results is emitted.
  return JSON.stringify([
    state.resource,
    state.interactivity ?? 'full',
    turns,
    state.activeTurn?.id,
    state.queuedMessages?.map(message => message.id) ?? [],
    state.steeringMessage?.id,
  ])
}
